import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { APP_TIMEZONE } from '@/shared/lib/dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { AppButton, AppSelect, AppSpin } from '@/components/atoms';
import AddVisitCard from '@/components/molecules/AddVisitCard';
import DateNavigator from '@/components/molecules/DateNavigator';
import VisitCard from '@/components/molecules/VisitCard';
import { useVisitList } from '@/hooks/massager/useVisitList';
import { useCancelVisit, useDoneVisit, useStartVisit } from '@/hooks/massager/useVisitMutations';
import { useGeolocation } from '@/hooks/massager/useGeolocation';
import { useMassagerClinicLocations } from '@/hooks/massager/useMassagerClinicLocations';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { MASSAGER_JA } from '@/shared/i18n/massager';
import { showVisit } from '@/services/massager/visit.service';
import useAppToast from '@/hooks/useAppToast';
import { isNotFoundError } from '@/utils/apiError';
import type { Visit } from '@/types/massager/visit';

const today = () => dayjs().tz(APP_TIMEZONE).format('YYYY-MM-DD');

/** Sentinel value used by the すべて option (AppSelect strips real null). */
const ALL_LOCATIONS = -1;

/**
 * /{site}/massager/home/:date?
 * Visit-list landing page for a massager: date navigation, visit cards,
 * and an inline add-visit card that can be opened either by name search or
 * by GPS (which pre-seeds the current location into the create payload).
 */
const CheckinListPage: React.FC = () => {
  const { date, site } = useParams<{ date?: string; site: string }>();
  const navigate = useNavigate();
  const { serviceType } = useTheme();

  const initialDate = useMemo(() => {
    const candidate = date ?? today();
    return dayjs(candidate, 'YYYY-MM-DD', true).isValid() ? candidate : today();
  }, [date]);

  const [targetDate, setTargetDate] = useState<string>(initialDate);
  const [selectedLocationId, setSelectedLocationId] = useState<number>(ALL_LOCATIONS);

  const handleDateChange = (next: string) => {
    setTargetDate(next);
    setAddOpen(false);
    setSeedCoords(null);
    setGpsTriggerKey(null);
    setGpsErrorMessage(null);
    navigate(`/${site ?? 'medix'}/massager/home/${next}`);
  };

  const themedServiceType =
    serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_R2 ? serviceType : null;

  const { locations } = useMassagerClinicLocations({
    date: targetDate,
    serviceType: themedServiceType,
  });

  // Reset filter to すべて when the option list changes shape (different
  // service / date) so a stale id doesn't leave the user with zero visits.
  useEffect(() => {
    if (
      selectedLocationId !== ALL_LOCATIONS &&
      !locations.some(l => l.clinic_location_id === selectedLocationId)
    ) {
      setSelectedLocationId(ALL_LOCATIONS);
    }
  }, [locations, selectedLocationId]);

  const { visits, isLoading, refetch } = useVisitList(targetDate, themedServiceType);

  const filteredVisits = useMemo(() => {
    if (selectedLocationId === ALL_LOCATIONS) return visits;
    return visits.filter(v => v.clinic_location_id === selectedLocationId);
  }, [visits, selectedLocationId]);

  const startMut = useStartVisit();
  const doneMut = useDoneVisit();
  const cancelMut = useCancelVisit();
  const geo = useGeolocation();
  const toast = useAppToast();
  const { clinic } = useMassagerAuthContext();
  const siteType = clinic?.site_type ?? site ?? 'medix';

  /**
   * Pre-flight the remarks (edit) page navigation: if the visit has been
   * soft-deleted by another actor since this list rendered, show the spec
   * toast and refetch the list instead of navigating to a 404 edit screen.
   */
  const handleMemoClick = async (visit: Visit) => {
    try {
      await showVisit(siteType, visit.id, visit.service_type);
    } catch (err) {
      if (isNotFoundError(err)) {
        toast.error('変更ができませんでした。');
        refetch();
        return;
      }
      // Non-404 errors fall through to the navigation; the remarks page
      // surfaces its own fetch-error toast when it loads.
    }
    navigate(`/${site ?? 'medix'}/massager/checkin/remarks/${visit.id}`, {
      state: { serviceType: visit.service_type, date: targetDate },
    });
  };

  // Add-visit card state — the card stays MOUNTED across open/close so the
  // form data the massager already typed is preserved when toggling. Only the
  // outer wrapper hides via CSS. (Per spec HIT-2329: 「閉じる時に入力済みデータは
  // 消さない」.)
  const [addOpen, setAddOpen] = useState(false);
  const [seedCoords, setSeedCoords] = useState<{ lat: number; lng: number } | null>(null);
  // Bumped each time the massager clicks GPSで追加 with successful geolocation;
  // forwarded to AddVisitCard so its GPS-search effect re-fires even when the
  // resolved coords match a previous request.
  const [gpsTriggerKey, setGpsTriggerKey] = useState<string | null>(null);
  // Geolocation error message (denied/unavailable/timeout) — surfaced inside
  // the card per spec instead of at the bottom of the page.
  const [gpsErrorMessage, setGpsErrorMessage] = useState<string | null>(null);
  const addCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (addOpen) {
      addCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [addOpen, seedCoords]);

  const busy = startMut.isPending || doneMut.isPending || cancelMut.isPending || geo.isFetching;

  const handleOpenByName = () => {
    // Toggle: tap once to open the by-name card, tap again to close it.
    setAddOpen(prev => {
      const next = !prev;
      // Clear any leftover GPS error when reopening so the card doesn't
      // show 位置情報取得できませんでした between unrelated opens.
      if (next) setGpsErrorMessage(null);
      return next;
    });
  };

  const handleOpenByGps = async () => {
    if (addOpen) {
      // Already open — close, but preserve form data + seed coords so a later
      // 受診者名で追加 toggle reopens to the same state.
      setAddOpen(false);
      return;
    }
    try {
      const coords = await geo.request();
      setSeedCoords({ lat: coords.latitude, lng: coords.longitude });
      setGpsErrorMessage(null);
      setGpsTriggerKey(String(Date.now()));
      setAddOpen(true);
    } catch (err) {
      // Show the geolocation message inside the card per spec.
      setGpsErrorMessage(err instanceof Error ? err.message : null);
      setSeedCoords(null);
      setAddOpen(true);
    }
  };

  const locationOptions = useMemo(
    () => [
      { value: ALL_LOCATIONS, label: 'すべて' },
      ...locations.map(l => ({
        value: l.clinic_location_id,
        label: l.address ? `${l.location_name} ー ${l.address}` : l.location_name,
      })),
    ],
    [locations]
  );

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="z-20 -mx-3 -mt-4 bg-gray-50 px-3 pt-4 pb-2 portrait:sticky portrait:top-0 sm:-mx-4 sm:-mt-6 sm:px-4 sm:pt-6">
        <div className="overflow-hidden rounded-md bg-white shadow-sm">
          <DateNavigator value={targetDate} onChange={handleDateChange} />
          <div className="px-2 py-3 sm:px-3">
            <div className="flex flex-row items-center gap-2 bg-gray-100 px-2 py-3 sm:px-3 rounded-md">
              <label className="shrink-0 text-sm text-gray-800" htmlFor="location-filter">
                施設
              </label>
              <AppSelect
                id="location-filter"
                size="large"
                value={selectedLocationId}
                onChange={value => setSelectedLocationId(value as number)}
                options={locationOptions}
                className="flex-1 min-w-0"
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <AppSpin />
        </div>
      )}

      {!isLoading && filteredVisits.length === 0 && (
        <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-sm text-gray-500">
          {MASSAGER_JA.empty}
        </div>
      )}

      {!isLoading && filteredVisits.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filteredVisits.map(visit => (
            <VisitCard
              key={visit.id}
              visit={visit}
              onMemoClick={() => handleMemoClick(visit)}
              onStart={id => startMut.mutate({ visitId: id, serviceType: visit.service_type })}
              onDone={id => doneMut.mutate({ visitId: id, serviceType: visit.service_type })}
              onCancel={id => cancelMut.mutate({ visitId: id, serviceType: visit.service_type })}
              busy={busy}
            />
          ))}
        </div>
      )}

      {/* Add-visit card — kept mounted across open/close so form data the
          massager already typed isn't wiped when they toggle the card off and
          back on. We hide via CSS instead of unmounting. key={targetDate}
          resets all form state when the date changes (per spec: switching
          dates should close the card and clear entered data). */}
      <div key={targetDate} ref={addCardRef} className={addOpen ? '' : 'hidden'}>
        <AddVisitCard
          targetDate={targetDate}
          initialLatitude={seedCoords?.lat ?? null}
          initialLongitude={seedCoords?.lng ?? null}
          gpsTriggerKey={gpsTriggerKey}
          gpsErrorMessage={gpsErrorMessage}
          existingVisits={visits}
          onCreated={() => {
            setAddOpen(false);
            setSeedCoords(null);
            setGpsTriggerKey(null);
            setGpsErrorMessage(null);
            void refetch();
          }}
        />
      </div>
      <div className="sticky bottom-0 z-[1100] -mx-3 -mb-4 mt-auto flex flex-row gap-2 border-t border-gray-200 bg-white px-3 py-3 shadow-lg sm:static sm:z-auto sm:mx-0 sm:mb-0 sm:mt-0 sm:justify-center sm:border-none sm:bg-transparent sm:p-0 sm:shadow-none">
        <AppButton
          block
          type="primary"
          size="large"
          onClick={handleOpenByName}
          disabled={busy}
          className="!min-h-11 sm:!w-auto sm:!min-w-[180px]"
        >
          受診者名で追加
        </AppButton>
        <AppButton
          block
          type="primary"
          size="large"
          onClick={handleOpenByGps}
          loading={geo.isFetching}
          disabled={busy}
          className="!min-h-11 sm:!w-auto sm:!min-w-[180px]"
        >
          {MASSAGER_JA.action.gpsAdd}
        </AppButton>
      </div>
    </div>
  );
};

export default CheckinListPage;
